import LegalPage from './LegalPage';

const CONTENT = `# SiteScope — Privacy Policy

**Last updated: 27 August 2026**

This Privacy Policy describes how SiteScope PropTech ABN 22 740 236 438 ("we", "us", "our") collects, uses, discloses, and protects personal information in connection with the SiteScope platform ("Service"). We are committed to complying with the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).

---

## 1. Information We Collect

### 1.1 Information you provide to us

When you create an account or subscribe to the Service, we collect:

- Email address
- Name
- Password (stored securely by our authentication provider)
- Payment information (processed and stored by Stripe — we do not store your credit card details)

For Pro tier subscribers, we also collect:

- Australian Business Number (ABN)
- Builder licence number or architect registration number
- Business name and contact details

### 1.2 Information collected automatically

When you use the Service, we automatically collect:

- Device information (browser type, operating system)
- Usage data (pages viewed, features used, watch zones configured)
- IP address
- Cookies and similar technologies for session management and preferences
- Website analytics data collected through Google Analytics on designated public council information pages, including page visits, approximate location, browser and device information, and a pseudonymous client identifier. Google Analytics does not run in the authenticated SiteScope app, and URL query strings are excluded from page and referrer locations sent to Google.

### 1.3 Information from public sources

SiteScope aggregates publicly available information from government sources, including:

- Development application data from local council websites (Byron Shire, Ballina Shire, Lismore City, and Tweed Shire councils)
- Development application data from the NSW Planning Portal Open Data API

This public data may include:

**Standard tier (displayed to all users):**

- Property addresses associated with development applications
- DA lodgement dates, status, and determination outcomes
- Development descriptions and categories
- Cost of development (where available from government source)
- Links to the relevant council's own DA detail page

**Pro tier (displayed to verified construction professionals only):**

- Applicant names (as listed on publicly available DA documents)
- Builder, architect, and other professional names (as listed on publicly available DA documents)
- Business contact details of construction professionals (as extracted from publicly available government documents)
- Brokered introduction request records, where a Pro user asks SiteScope to contact an owner/applicant on their behalf

We do not display residential phone numbers or personal email addresses of homeowners to users. Where such details are present in publicly available DA records, SiteScope may process them internally to operate a brokered introduction workflow, opt-out suppression, audit logging, and compliance controls. We do not display financial information of applicants or any information not already publicly available through council DA tracking systems or the NSW Planning Portal.

AI services may be used to analyse publicly available development application documents to structure data for display in the Service. No user account data is sent to AI services — only publicly available government documents.

## 2. How We Use Your Information

We use the information we collect to:

- Provide, maintain, and improve the Service
- Process your subscription and payments
- Send you alerts, notifications, and email digests you have opted into
- Verify your identity and business credentials (Pro tier)
- Analyse usage patterns to improve the Service
- Respond to your enquiries and support requests
- Comply with legal obligations

We do not sell your personal information to third parties.

## 3. Legal Basis for Processing Public DA Data

Development application information displayed on SiteScope is classified as "open access information" under the Government Information (Public Access) Act 2009 (NSW) ("GIPA Act"). Councils are required to make this information publicly available as part of their obligations under the GIPA Act.

Where data is sourced from the NSW Planning Portal Open Data API, it is provided under a Creative Commons Attribution 4.0 International (CC BY 4.0) licence, which expressly permits commercial use, redistribution, and adaptation with attribution.

For Pro tier contact information extracted from publicly available PDF documents attached to development applications on council websites, we rely on the following basis:

- The documents are publicly available government records
- The information relates to individuals acting in their professional capacity (builders, architects, planners) rather than in a private capacity
- Access is restricted to verified construction industry professionals
- Users are contractually prohibited from using contact information for harassment, cold calling of homeowners, or on-selling data
- Owner/applicant personal contact points, where present in public records, are not displayed directly and are used only for brokered SiteScope introductions with opt-out controls

## 4. Disclosure of Information

We use SOC 2 compliant third-party service providers to operate the Service. These providers may process limited personal information on our behalf as required to deliver the Service:

- A PCI-DSS compliant payment processor for subscription payments
- A cloud database provider with data hosted in Australia (Sydney region)
- A cloud infrastructure provider for application hosting
- A transactional email provider for alerts and notifications
- A third-party mapping service for map display (no personal data shared)
- Google Analytics for aggregate traffic measurement on designated public council information pages
- AI services for analysing publicly available government documents (no user account data shared)

We may also disclose information if required by law, regulation, court order, or lawful government request.

We do not sell, rent, or trade your personal information to third parties for marketing purposes.

## 5. Data Security

We take reasonable steps to protect your personal information from misuse, interference, loss, unauthorised access, modification, or disclosure. These measures include:

- Encryption of data in transit (HTTPS/TLS)
- Row-level security policies on our database
- Hashed password storage
- Verified business credentials for Pro tier access
- Regular security reviews

No method of transmission over the internet is completely secure. While we strive to protect your personal information, we cannot guarantee its absolute security.

### Data breach notification

In the event of a data breach likely to result in serious harm, we will notify affected users and the Office of the Australian Information Commissioner (OAIC) as required under the Notifiable Data Breaches scheme (part of the Privacy Act 1988 (Cth)).

## 6. Data Retention

We retain your account information for as long as your account is active or as needed to provide the Service. If you close your account, we will delete your personal information within 30 days, except where we are required to retain it for legal or regulatory purposes.

Publicly sourced DA data is retained indefinitely as part of the Service's historical record and analysis features.

## 7. Your Rights

Under the Australian Privacy Principles, you have the right to:

- **Access** your personal information held by us
- **Correct** inaccurate or incomplete personal information
- **Request deletion** of your personal information (subject to legal retention requirements)
- **Opt out** of marketing communications at any time
- **Complain** if you believe we have breached the Australian Privacy Principles

To exercise any of these rights, contact us at the details below.

## 8. Cookies

We use cookies and similar technologies to maintain your session, remember your preferences, and improve the Service. On designated public council information pages, this includes Google Analytics first-party cookies (including \`_ga\` and a property-specific \`_ga_...\` cookie) that help us measure visits and sessions.

Google Analytics processes pseudonymous usage information from those public pages on our behalf. It is not loaded in the authenticated app, and we use it for audience measurement rather than personalised advertising. You can control or delete cookies through your browser settings, but disabling cookies may affect measurement and some Service functionality.

## 9. Third-Party Links

The Service contains links to council websites and other third-party websites. We are not responsible for the privacy practices of those websites. We encourage you to read the privacy policies of any third-party website you visit.

## 10. Children's Privacy

The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.

## 11. Changes to This Policy

We may update this Privacy Policy from time to time. Material changes will be notified to you via email prior to taking effect. The updated policy will be posted on the Service with the revised "Last updated" date.

## 12. Complaints

If you believe we have breached the Australian Privacy Principles, you may lodge a complaint with us at the contact details below. We will respond to your complaint within 30 days.

If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au.

## 13. Contact

For questions about this Privacy Policy or to exercise your privacy rights, contact us at:

**SiteScope PropTech**
ABN: 22 740 236 438
Email: support@sitescopeapp.com
Website: sitescopeapp.com`;

export default function PrivacyPage() {
  return <LegalPage title="Privacy Policy" content={CONTENT} />;
}
